import {
  Component, DestroyRef, ElementRef,
  EventEmitter,
  inject,
  Input,
  Output, signal,
  SimpleChanges, ViewChild,
} from '@angular/core';
import { ImportsModule } from './imports';
import {HttpClient} from '@angular/common/http';
import {FormGroup, FormControl, Validators, AbstractControl} from '@angular/forms';
import {
  Observable,
  BehaviorSubject,
  first,
  catchError,
  of,
  tap,
  map, throwError, EMPTY,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import {AddEmail, IFields, ITerritory, PatchUserData} from '../../../app/core/interfaces';
import { AuthService } from '../../../app/core/services/auth/auth.service';
import { TimerService } from '../../../app/core/services/timer/timer.service';
import { UserPatchModel } from '../../../app/core/models/patch-user.model';
import { UserDataService } from '../../../app/core/services/user-data/user-data.service';
import { fileTypeValidator } from '../../../app/core/validators/image.validator';
import { repeatPasswordValidator } from '../../../app/core/validators/repeat-password.validator';
import {MatInput} from "@angular/material/input";
import {switchMap} from "rxjs/operators";
import {ValidatorDateDirective} from "../../../app/core/directives/validators-datepicker.directive";
import {MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger} from "@angular/material/autocomplete";
import {dateRangeValidator} from "../../../app/core/validators/date-range.validator";
import {OnlyLettersDirective} from "../../../app/core/directives/only-letters.directive";
import {emailValidator} from "../../../app/core/validators/email.validator";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {validationMessage} from "../../../app/core/utils";
import {NoWhitespaceDirective} from "../../../app/core/directives/no-write-space.directive";


@Component({
  selector: 'required-fields-form',
  imports: [ImportsModule, ValidatorDateDirective, MatInput, MatAutocompleteTrigger, MatAutocomplete, OnlyLettersDirective, NoWhitespaceDirective,],
  templateUrl: './required-fields-form.component.html',
  styleUrl: './required-fields-form.component.scss',
})
export class RequiredFieldsFormComponent {
  private timerService = inject(TimerService);
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private userDataService = inject(UserDataService);
  private today = new Date();
  private minDate = new Date(1900, 0, 1);
  private allRegions: ITerritory[] = [];
  private selectedRegion: ITerritory | null = null;
  private isFormGenerated = false;
  private destroy = inject(DestroyRef);

  public errorMessage = signal<string|null>(null)
  public isFocusedEmailCode: boolean = false;
  public isFocusedEmail: boolean = false;
  public regionPanelIsOpen: boolean = false;
  public timer = this.timerService.timer;
  public timerStatus = this.timerService.timerStatus;
  public userForm: FormGroup = new FormGroup({});
  public options$ = this.http.get<any>(
    environment.ApiUrl + '/api/v1.0/dictionary/territory'
  );
  public regionSearchControl = new FormControl<string | null>('', [Validators.required]);
  public filteredRegions: ITerritory[] = [];
  public codeWasSent$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public arrowFieldState = signal<boolean>(false)
  @Input() fields: IFields = {};
  @Output() fieldsPassed: EventEmitter<void> = new EventEmitter();
  @Output() fieldsSavedSuccess: EventEmitter<boolean> = new EventEmitter(false);

  @ViewChild('regionInput', { static: false }) regionInputRef!: ElementRef<HTMLInputElement>;

  ngOnChanges(changes: SimpleChanges) {
    if (!this.isFormGenerated) {
      if (changes['fields']) {
        this.userForm = this.createNewForm(changes['fields'].currentValue);
        this.isFormGenerated = true;
      }
    }
  }


  ngOnInit(): void {
    this.options$
      .pipe(takeUntilDestroyed(this.destroy)).subscribe(options => {
      this.allRegions = options?.data?.regions ?? [];
      this.filteredRegions = [...this.allRegions];
    });

    this.regionSearchControl.valueChanges.pipe(takeUntilDestroyed(this.destroy)).subscribe(searchTerm  => {
      const value = typeof searchTerm === 'string' ? searchTerm.toLowerCase() : '';
      this.filteredRegions = this.allRegions.filter(region =>
        region.title.toLowerCase().includes(value)
      );
    });
  }

  onRegionSelected(event: MatAutocompleteSelectedEvent, controlName: string) {
    const region = event.option.value;
    this.selectedRegion = region;
    this.regionSearchControl.setValue(region);
    this.userForm.get(`personalInfo.${controlName}`)!.setValue(event.option.value);

    setTimeout(() => {
      this.regionInputRef?.nativeElement.blur();
    });
  }

  onRegionInputFocus(): void {
    this.regionPanelIsOpen = true;
    this.userForm.get(`personalInfo.region`)!.setValue(this.regionSearchControl.value)
    this.selectedRegion = null;
    this.regionSearchControl.setValue(null);
  }

  ngOnDestroy() {
    this.timerService.stopTimer();
  }

  private passForm(name: 'email' | 'photo' | 'personalInfo' | 'password') {
    this.userForm.removeControl(name);
    delete this.fields[name];

    this.authService.setConsentStatus({ fieldConsent: this.fields });

    if (JSON.stringify(this.userForm.value) == JSON.stringify({})) {
      this.fieldsSavedSuccess.emit(true);
      this.fieldsPassed.emit();
    }
  }

  public getErrorType(
    controlName: string,
    parentGroup?: string,
    grandParent?: string
  ): string | null {
    let control;

    if (grandParent) {
      control = this.userForm
        .get(grandParent)
        ?.get(parentGroup!)
        ?.get(controlName);
    } else if (parentGroup) {
      control = this.userForm.get(parentGroup)?.get(controlName);
    } else {
      control = this.userForm.get(controlName);
    }

    const errorName = control?.errors ? Object.keys(control.errors)[0] : null;

    if (control?.touched && errorName) {
      return validationMessage(errorName);
    }

    return null;
  }

  public isRequired(controlName: string, parentGroup?: string, grandParent?: string): boolean {
    let control;

    if (grandParent) {
      control = this.userForm
        .get(grandParent)
        ?.get(parentGroup!)
        ?.get(controlName);
    } else if (parentGroup) {
      control = this.userForm.get(parentGroup)?.get(controlName);
    } else {
      control = this.userForm.get(controlName);
    }

    return !!control?.validator?.({} as AbstractControl)?.['required'];
  }

  public displayRegion(region: ITerritory): string {
    return region?.title ?? '';
  }

  public onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files?.length) {
      const file = input.files[0];
      this.userForm.get('photo.photo')?.markAsTouched();
      this.userForm.get('photo.photo')?.setValue(file);
    }
  }

  public uploadPhoto(): void {
    if (this.userForm.get('photo.photo')?.invalid) {
      this.errorMessage.set(
        'Пожалуйста, выберите файл и убедитесь, что он соответствует требованиям.'
      );
      return;
    }

    const formData = new FormData();
    formData.append('file', this.userForm.get('photo.photo')?.value);

    this.userDataService
      .uploadImage(formData)
      .pipe(first())
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
        next: () => {
          this.errorMessage.set(null);
          this.passForm('photo');
        },
        error: (err) => {
          if (err.status === 400) {
            this.errorMessage.set(
              err.error?.errors?.by_field?.file?.[0] ??
              err.error?.errors?.common?.[0] ??
              'Ошибка при загрузке фотографии.'
            );
          } else if (err.status === 403) {
            this.errorMessage.set(
              err.error?.errors?.common?.[0] ?? 'Ошибка при загрузке фотографии.'
            );
          } else {
            this.errorMessage.set('Ошибка при загрузке фотографии.');
          }
        },
      });
  }

  private createNewForm(fields: IFields): FormGroup {
    const form = new FormGroup({});

    Object.keys(fields).forEach((groupName) => {
      const group = fields[groupName];

      if ('fields' in group) {
        const nestedGroup = new FormGroup({});


        group.fields.forEach((field) => {
          if ('children' in field) {
            const childGroup = this.createChildGroup(field);
            nestedGroup.addControl(field.name, childGroup);
          } else {
            const control = this.getFormControl(field);
            if (control) {
              nestedGroup.addControl(field.name, control);
            }
          }
        });


        form.addControl(groupName, nestedGroup);
      } else {

        const control = this.getFormControl(group);
        if (control) {
          form.addControl(groupName, control);
        }
      }
    });

    if (form.get('password')) {
      form.get('password')?.addValidators(repeatPasswordValidator());
    }
    return form;
  }

  private createChildGroup(field: any): FormGroup {
    const childGroup = new FormGroup({});

    field.children?.forEach((childField: any) => {
      const childControl = this.getFormControl(childField);
      if (childControl) {
        childGroup.addControl(childField.name, childControl);
      }
    });

    return childGroup;
  }

  private getFormControl(field: any): FormControl | null {
    const fieldValidators: { [key: string]: any[] } = {
      phone: [Validators.required, Validators.pattern(/^\+7\d{10}$/)],
      email: [Validators.required, emailValidator(), Validators.maxLength(50)],
      code: [Validators.required, Validators.maxLength(4), Validators.minLength(4)],
      birth_date: [Validators.required, Validators.minLength(10), dateRangeValidator(this.minDate, this.today)],
      region: [Validators.required],
      repeatPassword: [Validators.required],
      photo: [
        Validators.required,
        fileTypeValidator(['image/png', 'image/jpeg', 'image/jpg', 'application/pdf']),
      ],
      password: [Validators.required, Validators.minLength(7)],
      firstName: [Validators.required, Validators.maxLength(30)],
      middleName: [Validators.maxLength(30)],
      lastName: [Validators.required, Validators.maxLength(30)],
    };

    if (fieldValidators[field.name]) {
      const validators = fieldValidators[field.name];
      return new FormControl(field.value ?? '', validators);
    }

    return null;
  }

  public addPassword() {
    if (this.userForm.get('password')?.invalid) {
      return;
    }
    const password = this.userForm.get('password.password')?.value;
    if (password) {
      this.userDataService.addPassword(password)
        .pipe(takeUntilDestroyed(this.destroy))
        .subscribe({
        next: () => {
          this.errorMessage.set(null);
          this.passForm('password');
        },
        error: (err) => {
          if (err.status === 400) {
            this.errorMessage.set(
              err.error?.errors?.by_field?.password?.[0] ?? 'Пароль не был установлен'
            );
          } else if (err.status === 403 || err.status === 404) {
            this.errorMessage.set(
              err.error?.errors?.common?.[0] ?? 'Пароль не был установлен'
            );
          } else {
            this.errorMessage.set('Пароль не был установлен');
          }
        },
      });
    }
  }

  private startTimer(value: number) {

    if (value > 0) {
      this.timerService.startTimer(value);
    }
  }

  public backToEmail() {
    this.errorMessage.set(null);
    this.codeWasSent$.next(false);
    this.timerService.stopTimer();
    this.timerService.startTimer(0);
  }

  public updateUserData() {
    const userData = this.userForm.get('personalInfo')?.value;
    const bodyModel: PatchUserData = new UserPatchModel(userData);

    this.userDataService.updateUserData(bodyModel)
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
      next: () => {
        this.errorMessage.set(null);
        this.passForm('personalInfo');
      },
      error: (err) => {
        if (err.status === 400) {
          this.errorMessage.set(
            err.error?.errors?.by_field?.name?.last?.[0] ??
            err.error?.errors?.by_field?.name?.first?.[0] ??
            err.error?.errors?.by_field?.name?.middle?.[0] ??
            err.error?.errors?.by_field?.birth_date?.[0] ??
            err.error?.errors?.by_field?.territory?.okato?.[0] ??
            err.error?.errors?.common?.[0] ??
            'Ошибка при обновлении данных.'
          );
        } else if (err.status === 403) {
          this.errorMessage.set(
            err.error?.errors?.common?.[0] ?? 'Ошибка при обновлении данных.'
          );
        } else {
          this.errorMessage.set('Ошибка при обновлении данных.');
        }
      },
    });
  }

  public regionPanelBlur() {
    setTimeout(() => {
      if (!this.selectedRegion) {
        this.regionSearchControl.setValue(this.userForm.get(`personalInfo.region`)!.value);
      }

      if (this.regionSearchControl.value?.length === 0 || !this.regionSearchControl.value) {
        this.userForm.get(`personalInfo.region`)?.setValue(null);
        this.regionSearchControl.setValue(null);
        this.regionSearchControl.markAsTouched();
        this.userForm.get(`personalInfo.region`)?.markAsTouched();
      }

      setTimeout(() => {
        if (!document.querySelector('.cdk-overlay-pane')) {
          this.regionPanelIsOpen = false;
        }
        const value: string = this.regionSearchControl.value ?? '';
        const match = this.filteredRegions.find(
          (region) => region.title.toLowerCase() === value.toLowerCase()
        );
        if (!match) {
          this.regionSearchControl.setValue(null);
        } else {
          this.regionSearchControl.setValue(match.title);
        }
        this.regionPanelIsOpen = false;
      }, 50);
    }, 150);
  }

  public addEmailAndRequestConfirm(again?:boolean): void {
    const email = this.getEmailInput();
    if (!email) {
      this.errorMessage.set('Email не прошел валидацию');
      return;
    }

    if(again){
      this.userForm.get('email.code')?.setValue("")
    }


    this.tryAddEmail(email).pipe(
      first(),
      catchError((error) => {
        if (error?.err?.status === 400) {
          this.errorMessage.set(error.err.error?.errors?.by_field?.contact?.[0] ?? null);

          return EMPTY;
        }

        if (error?.err?.status === 403 || error?.err?.status === 404 || error?.err?.status === 409) {
          this.errorMessage.set(error.err.error?.errors?.common?.[0] ?? null);

          return EMPTY;
        }

        return of(false);
      }),
      switchMap((added) =>
        this.tryRequestConfirmation(email).pipe(
          first(),
          map((confirmationRequested) => ({ added, confirmationRequested }))
        )
      )
    ).pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
        next: ({ added, confirmationRequested }) => {
          this.processAddAndConfirmResults(added, confirmationRequested, email);
        },
        error: (error:AddEmail) => {
          this.processAddAndConfirmResults(false, false, email, error);
        }
      })
  }

  private getEmailInput(): string | null {
    const control = this.userForm.get('email.email');
    const email = control?.value;
    return control?.valid && email ? email : null;
  }

  private tryAddEmail(email: string): Observable<boolean> {
    return this.userDataService.addEmailForUserData(email).pipe(
      map(() => true),
      catchError((err) => {
        this.handleAddError(err, email)
          return throwError(() => ({ type: 'add', err, email }));
      })
    );
  }

  private tryRequestConfirmation(email: string): Observable<boolean> {
    return this.userDataService.requestForConfirmEmail(email).pipe(
      tap((response) => {
        const retryAfter = Number(response.headers?.get('Retry-After'));
        if (retryAfter) {
          this.startTimer(retryAfter);
        }
      }),
      map(() => true),
      catchError((err) => {
        this.handleConfirmError(err)
        return throwError(():AddEmail => ({ type: 'confirm', err }));
      })
    );
  }

  private processAddAndConfirmResults(
    added: boolean,
    confirmationRequested: boolean,
    email: string,
    error?: AddEmail,
  ): void {

    if (error) {
      const status = error.err.status;

      if (status === 422) {
        this.handleAlreadyExistingEmail(email);
        return;
      }

      if (status === 429) {
        this.errorMessage.set(
          error.err.error?.errors?.common?.[0] ??
          'Слишком частые запросы кодов верификации. Попробуйте позже.'
        );
        return;
      }
      return;
    }

    if (added && confirmationRequested) {
      this.codeWasSent$.next(true);
      this.errorMessage.set(null);
      return;
    }

    if (!added && confirmationRequested) {
      this.handleAlreadyExistingEmail(email);
      return;
    }

    if (added && !confirmationRequested) {
      this.errorMessage.set('Email добавлен, но запрос на подтверждение не выполнен');
      return;
    }

  }

  private handleAlreadyExistingEmail(email: string): void {
    this.codeWasSent$.next(true);

    this.getExistingEmail(email)
      .pipe(
        first(),
        tap((existing) => {
          if (existing?.confirmed) {
            this.errorMessage.set(null);
            this.passForm('email');
          } else {
            this.errorMessage.set(null);
          }
        }),
        catchError(() => {
          this.errorMessage.set('Ошибка при получении данных пользователя.');
          return of(null);
        })
      ).pipe(takeUntilDestroyed(this.destroy))
      .subscribe();
  }

  private getExistingEmail(
    email: string
  ): Observable<any> {
    return this.userDataService.getUserData().pipe(
      first(),
      map((u) => u.data.person.contacts.emails.find((e) => e.email === email))
    );
  }

  private handleAddError(err: any, email: string): Observable<boolean> {
    if (err.status !== 422) {
      return of(false);
    }

    return this.getExistingEmail(email).pipe(
      tap((existing) => {
        if (!existing) {
          this.errorMessage.set(
            'Этот контакт уже добавлен, но его статус неизвестен.'
          );
        } else if (existing.confirmed) {
          this.errorMessage.set(null);
          this.passForm('email');
        }
      }),
      map(() => false),
      catchError(() => {
        this.errorMessage.set('Ошибка при получении данных пользователя.');
        return of(false);
      })
    );
  }

  private handleConfirmError(err: any): Observable<boolean> {
    if (err.status === 429) {
      const retryAfter = Number(err.headers?.get('Retry-After'));
      if (retryAfter) {
        this.startTimer(retryAfter);
      }
      this.errorMessage.set(
        err.error?.errors?.common?.[0] ??
        'Попытки проверки кода исчерпаны. Необходимо получить новый проверочный код'
      );
    } else if (err.status === 400) {
      this.errorMessage.set(
        err.error?.errors?.by_field?.contact?.[0] ?? 'Не удалось отправить код подтверждения.'
      );
    } else {
      this.errorMessage.set(
        err.error?.errors?.common?.[0] ?? 'Не удалось отправить код подтверждения.'
      );
    }
    return of(false);
  }

  public cofirmEmailWithCode() {
    const email = this.userForm.get('email.email')?.value;
    const code = this.userForm.get('email.code')?.value;

    if (email && code) {
      this.userDataService
        .confirmEmail(email, code)
        .pipe(first())
        .pipe(takeUntilDestroyed(this.destroy))
        .subscribe({
          next: () => {
            this.errorMessage.set(null);
            this.passForm('email');
          },
          error: (err) => {
            if (err.status === 400) {
              this.errorMessage.set(
                err.error?.errors?.by_field?.contact?.[0] ?? 'Неверный код'
              );
            } else {
              this.errorMessage.set(err.error?.errors?.common?.[0] ?? 'Неверный код');
            }
          },
        });
    } else {
      this.errorMessage.set('Email или код не указаны');
    }
  }

  public inputEmail() {
    if(this.errorMessage()){
      this.errorMessage.set(null);
    }
  }

  public arrowMouseEnter() {
    this.arrowFieldState.set(true)
  }
  public arrowMouseLeave(){
    this.arrowFieldState.set(false)
  }

}
